import React, { useEffect, useMemo, useState } from "react";
import { Star, Trash2, Upload } from "lucide-react";
import { supabase } from "../supabaseClient";
import AdminTopBar from "./AdminTopBar";
import {
  FEATURED_EVENTS_LIMIT,
  loadFeaturedEventsConfig,
  readFeaturedEventsConfig,
  writeFeaturedEventsConfig,
} from "../utils/featuredEvents";

const FeaturedEvents = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [events, setEvents] = useState([]);
  const [featuredItems, setFeaturedItems] = useState(() => readFeaturedEventsConfig());

  const [selectedEventId, setSelectedEventId] = useState("");
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState("");
  const [message, setMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    let isMounted = true;

    const fetchEvents = async () => {
      const { data, error } = await supabase
        .from("events")
        .select("id, title, date, time, venue, image_url, status")
        .neq("status", "archived")
        .order("date", { ascending: true });

      if (error) {
        console.error("Failed to fetch events for featured list:", error);
        setErrorMessage("Failed to load events.");
        setLoading(false);
        return;
      }

      if (!isMounted) return;

      const nextEvents = data || [];
      setEvents(nextEvents);

      let sharedFeaturedItems = readFeaturedEventsConfig();
      try {
        sharedFeaturedItems = await loadFeaturedEventsConfig();
      } catch (loadError) {
        console.error("Failed to load featured events config:", loadError);
      }

      if (!isMounted) return;

      const currentEventIds = new Set(nextEvents.map((eventRow) => String(eventRow.id)));
      const matchedFeaturedItems = sharedFeaturedItems.filter((item) =>
        currentEventIds.has(String(item.eventId))
      );

      if (matchedFeaturedItems.length !== sharedFeaturedItems.length) {
        try {
          const saved = await writeFeaturedEventsConfig(matchedFeaturedItems);
          if (isMounted) setFeaturedItems(saved);
        } catch (saveError) {
          console.error("Failed to clear featured events that are no longer available:", saveError);
          if (isMounted) setFeaturedItems(matchedFeaturedItems);
        }
      } else if (isMounted) {
        setFeaturedItems(matchedFeaturedItems);
      }

      setLoading(false);
    };

    fetchEvents();

    return () => {
      isMounted = false;
    };
  }, []);

  const eventOptions = useMemo(() => {
    return events.map((eventRow) => ({
      id: String(eventRow.id),
      title: eventRow.title || "Untitled Event",
      date: eventRow.date || "",
      time: eventRow.time || "",
      venue: eventRow.venue || "-",
      imageUrl: eventRow.image_url || "",
    }));
  }, [events]);

  const featuredRows = useMemo(() => {
    return featuredItems
      .map((item) => {
        const match = eventOptions.find((eventRow) => eventRow.id === String(item.eventId));
        if (!match) return null;

        return {
          ...item,
          event: match,
        };
      })
      .filter(Boolean);
  }, [featuredItems, eventOptions]);

  const uploadBannerImage = async (file) => {
    const fileName = `featured-${Date.now()}-${file.name}`;
    const { error } = await supabase.storage.from("event-images").upload(fileName, file);

    if (error) throw error;

    const { data } = supabase.storage.from("event-images").getPublicUrl(fileName);
    return data.publicUrl;
  };

  const clearFeedback = () => {
    setMessage("");
    setErrorMessage("");
  };

  const toFeaturedSaveErrorMessage = (error) => {
    const rawMessage = String(error?.message || "");
    const isRlsError = error?.code === "42501" || /row-level security policy/i.test(rawMessage);

    if (isRlsError) {
      return "Save blocked by Supabase Storage policy. Run echo_jham_web/supabase/featured_events_storage_policies.sql, then try again.";
    }

    if (rawMessage) {
      return `Failed to save featured event. ${rawMessage}`;
    }

    return "Failed to save featured event.";
  };

  const handleAddFeatured = async () => {
    clearFeedback();

    if (!selectedEventId) {
      setErrorMessage("Please select an event first.");
      return;
    }

    const currentEventIds = new Set(eventOptions.map((eventRow) => eventRow.id));
    const matchedFeaturedItems = featuredItems.filter((item) => currentEventIds.has(String(item.eventId)));

    if (
      matchedFeaturedItems.length >= FEATURED_EVENTS_LIMIT
      && !matchedFeaturedItems.some((item) => String(item.eventId) === String(selectedEventId))
    ) {
      setErrorMessage(`Only ${FEATURED_EVENTS_LIMIT} featured events are allowed.`);
      return;
    }

    setSaving(true);

    try {
      let bannerUrl = "";
      if (bannerFile) {
        bannerUrl = await uploadBannerImage(bannerFile);
      }

      const now = new Date().toISOString();
      const next = [...matchedFeaturedItems];
      const existingIndex = next.findIndex((item) => String(item.eventId) === String(selectedEventId));

      if (existingIndex >= 0) {
        next[existingIndex] = {
          ...next[existingIndex],
          bannerUrl: bannerUrl || next[existingIndex].bannerUrl || "",
          updatedAt: now,
        };
      } else {
        next.push({
          eventId: selectedEventId,
          bannerUrl,
          updatedAt: now,
        });
      }

      const saved = await writeFeaturedEventsConfig(next);
      setFeaturedItems(saved);
      setBannerFile(null);
      setBannerPreview("");
      setMessage("Featured event saved.");
    } catch (error) {
      console.error(error);
      setErrorMessage(toFeaturedSaveErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveFeatured = async (eventId) => {
    clearFeedback();
    setSaving(true);

    try {
      const next = featuredItems.filter((item) => item.eventId !== eventId);
      const saved = await writeFeaturedEventsConfig(next);
      setFeaturedItems(saved);
      setMessage("Featured event removed.");
    } catch (error) {
      console.error(error);
      setErrorMessage(toFeaturedSaveErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const moveItem = async (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= featuredItems.length) return;

    setSaving(true);

    try {
      const next = [...featuredItems];
      const [picked] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, picked);

      const saved = await writeFeaturedEventsConfig(next);
      setFeaturedItems(saved);
    } catch (error) {
      console.error(error);
      setErrorMessage(toFeaturedSaveErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-[#f3f3f3] min-h-screen text-gray-900">
      <AdminTopBar />

      <div className="p-4 sm:p-6 md:p-10">
        <div className="max-w-7xl mx-auto min-w-0">
        <h1 className="text-2xl md:text-[26px] font-black text-gray-900 tracking-tight uppercase italic mb-2">Featured Events</h1>
        <p className="text-xs text-gray-600 uppercase tracking-wide mb-6">
          Highlight up to {FEATURED_EVENTS_LIMIT} events on homepage with optional featured-only posters.
        </p>

        {message && (
          <div className="mb-4 rounded-lg border border-green-200 bg-green-50 text-green-700 px-4 py-3 text-sm font-semibold">
            {message}
          </div>
        )}

        {errorMessage && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 text-red-700 px-4 py-3 text-sm font-semibold">
            {errorMessage}
          </div>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
          <section className="xl:col-span-2 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-sm font-black uppercase tracking-wide mb-4">Set Featured Event</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-widest mb-2">
                  Select Event
                </label>
                <select
                  value={selectedEventId}
                  onChange={(event) => setSelectedEventId(event.target.value)}
                  className="w-full h-10 rounded-lg border border-gray-300 px-3 text-sm bg-white"
                >
                  <option value="">Choose an existing event...</option>
                  {eventOptions.map((eventOption) => (
                    <option key={eventOption.id} value={eventOption.id}>
                      {eventOption.title}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-widest mb-2">
                  Featured Banner (optional)
                </label>
                <label className="h-28 rounded-xl border border-dashed border-gray-300 bg-gray-50 flex items-center justify-center cursor-pointer hover:border-brand-maroon transition-colors">
                  <div className="text-center text-gray-500 text-xs font-semibold uppercase tracking-wide">
                    <Upload size={16} className="mx-auto mb-2" />
                    Upload custom featured poster
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      if (!file) return;
                      setBannerFile(file);
                      setBannerPreview(URL.createObjectURL(file));
                    }}
                  />
                </label>
              </div>

              {bannerPreview && (
                <img
                  src={bannerPreview}
                  alt="Featured poster preview"
                  className="w-full h-40 object-cover rounded-xl border border-gray-200"
                />
              )}

              <button
                onClick={handleAddFeatured}
                disabled={saving || loading}
                className="w-full h-10 rounded-lg bg-black text-white text-xs font-black uppercase tracking-wider disabled:opacity-60"
              >
                {saving ? "Saving..." : "Mark as Featured"}
              </button>
            </div>
          </section>

          <section className="xl:col-span-3 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-4">
              <h2 className="text-sm font-black uppercase tracking-wide min-w-0">Homepage Featured Order</h2>
              <p className="text-[11px] text-gray-500 uppercase tracking-wider shrink-0">
                {featuredRows.length}/{FEATURED_EVENTS_LIMIT}
              </p>
            </div>

            {loading ? (
              <div className="h-56 rounded-xl bg-gray-100 animate-pulse" />
            ) : featuredRows.length === 0 ? (
              <div className="h-56 rounded-xl border border-dashed border-gray-300 flex items-center justify-center text-sm text-gray-500">
                No featured events yet.
              </div>
            ) : (
              <div className="space-y-3">
                {featuredRows.map((row, index) => (
                  <article
                    key={row.eventId}
                    className="grid grid-cols-12 gap-3 border border-gray-200 rounded-xl p-3 items-center"
                  >
                    <div className="col-span-12 sm:col-span-3 lg:col-span-2">
                      <img
                        src={row.bannerUrl || row.event.imageUrl || "/assets/featured_event.jpg"}
                        alt={row.event.title}
                        className="w-full h-20 object-cover rounded-lg"
                      />
                    </div>

                    <div className="col-span-12 sm:col-span-6 lg:col-span-7 min-w-0">
                      <p className="text-xs font-black uppercase truncate">{row.event.title}</p>
                      <p className="text-[11px] text-gray-600 truncate">
                        {row.event.date || "-"} {row.event.time ? `| ${row.event.time}` : ""} • {row.event.venue}
                      </p>
                      <p className="text-[10px] text-gray-500 uppercase tracking-wider mt-1">
                        {row.bannerUrl ? "Custom banner uploaded" : "Using event poster"}
                      </p>
                    </div>

                    <div className="col-span-12 sm:col-span-3 lg:col-span-3 flex flex-wrap sm:justify-end gap-2">
                      <button
                        onClick={() => moveItem(index, index - 1)}
                        disabled={index === 0}
                        className="h-8 px-2 rounded border border-gray-300 text-[10px] font-black uppercase disabled:opacity-40"
                      >
                        Up
                      </button>
                      <button
                        onClick={() => moveItem(index, index + 1)}
                        disabled={index === featuredRows.length - 1}
                        className="h-8 px-2 rounded border border-gray-300 text-[10px] font-black uppercase disabled:opacity-40"
                      >
                        Down
                      </button>
                      <button
                        onClick={() => handleRemoveFeatured(row.eventId)}
                        className="h-8 px-2 rounded bg-red-600 text-white text-[10px] font-black uppercase inline-flex items-center gap-1"
                      >
                        <Trash2 size={12} /> Remove
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            )}

            <div className="mt-5 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-[11px] text-gray-600 flex items-center gap-2">
              <Star size={12} className="text-brand-maroon" />
              Homepage displays featured events from this list, in this order. An empty list shows no featured events on the homepage.
            </div>
          </section>
        </div>
        </div>
      </div>
    </div>
  );
};

export default FeaturedEvents;
